import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/Avatar';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { EditorRico, iconoDeArchivo, leerEditor, pesoLegible } from './piezas';
import { subirAdjuntos, usePublicar } from './api';

/**
 * Compositor — escribir en el muro.
 *
 * ## Lo que se publica aquí lo ve TODO el portal
 *
 * El muro es un canal global. La caja de «Comunicado» —segmentar por área o por
 * rol— se retiró el 2026-08-26 por decisión del área: una publicación dirigida a
 * unos pocos no encaja en él. Lo dirigido tiene dos caminos con su audiencia
 * natural, y el aviso de abajo los nombra.
 *
 * Lo que sí queda es firmar **a nombre del área**, que es otra cosa: no cambia a
 * quién llega, cambia quién la firma. Exige `Auth.isStaff()` **y**
 * `Auth.can('feed','manage')` — las dos —, como lo pidió Bienestar para que la
 * insignia OFICIAL no la use cualquiera. Ocultarlo aquí es cortesía: quien
 * decide es el servidor.
 *
 * ## El tope de caracteres
 *
 * El servidor aplica 10.000 sobre el **HTML**; aquí se cuenta el **texto**, que
 * es lo que la persona ve escribir. Con formato, ~6.000 caracteres escritos
 * podían superarlo y el error solo aparecía al pulsar Publicar. El contador se
 * enseña a partir del 80 %: antes solo estorbaría.
 */

/** Mismo tope que `MAX_CONTENIDO_PUBLICACION` en `content.py`. */
const MAX_CARACTERES = 10000;
/** A partir de aquí se enseña el contador. */
const AVISO_DESDE = 0.8;
/** El límite por adjunto que aplica el servidor. */
const MAX_ADJUNTO_MB = 25;
/** El límite de la imagen de portada. */
const MAX_IMAGEN_MB = 10;

const TIPOS_QUE_SE_PUEDEN_ELEGIR = [
  { clave: 'general', icono: 'bi-chat-dots-fill', etiqueta: 'General' },
  { clave: 'logro', icono: 'bi-trophy-fill', etiqueta: 'Logro' },
  { clave: 'bienestar', icono: 'bi-heart-pulse-fill', etiqueta: 'Bienestar' },
];

export function Compositor({ alPublicar }: { alPublicar: () => void }) {
  const yo = Auth.getUser();
  const puedeComunicar = Auth.can('feed', 'manage');
  const puedeFirmarComoArea = Auth.isStaff() && puedeComunicar;

  // Los dos destinos del aviso de alcance, cada uno con SU permiso.
  //
  // `feed:manage` —lo que enseña el aviso— no autoriza ninguno de los dos:
  // Comunidades exige el módulo `comunidades` y Comunicaciones exige
  // `notificaciones:manage` (los catorce endpoints de ese módulo, y desde el
  // 2026-09-15 también su entrada del PAGE_MAP). Ofrecer un atajo que rebota a
  // Inicio con «No tienes acceso» es peor que no ofrecerlo.
  const hayComunidades = Auth.hasModule('comunidades');
  const hayComunicaciones = Auth.can('notificaciones', 'manage');

  const editorRef = useRef<HTMLDivElement>(null);
  const [version, setVersion] = useState(0);
  const [largo, setLargo] = useState(0);
  const [tipo, setTipo] = useState('general');
  const [imagen, setImagen] = useState<File | null>(null);
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  const [comoArea, setComoArea] = useState(false);
  const [nombrePublico, setNombrePublico] = useState('');
  const [fallidos, setFallidos] = useState<File[] | null>(null);
  const [postFallido, setPostFallido] = useState<number | null>(null);

  const publicar = usePublicar();
  const entradaImagen = useRef<HTMLInputElement>(null);
  const entradaArchivos = useRef<HTMLInputElement>(null);

  // La vista previa es un blob del navegador; hay que revocarlo o la pestaña se
  // queda con la imagen en memoria hasta que se recargue.
  useEffect(() => {
    if (!imagen) {
      setVistaPrevia(null);
      return;
    }
    const url = URL.createObjectURL(imagen);
    setVistaPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [imagen]);

  const excede = largo > MAX_CARACTERES;
  const vacio = largo === 0;

  const elegirImagen = (f: File | undefined) => {
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(f.type)) {
      Utils.toast('Formato no permitido. Usa JPG, PNG, WEBP o GIF.', 'error');
      return;
    }
    if (f.size > MAX_IMAGEN_MB * 1024 * 1024) {
      Utils.toast(`La imagen supera el límite de ${MAX_IMAGEN_MB} MB`, 'error');
      return;
    }
    setImagen(f);
  };

  const elegirArchivos = (lista: FileList | null) => {
    const nuevos: File[] = [];
    for (const f of Array.from(lista ?? [])) {
      if (f.size > MAX_ADJUNTO_MB * 1024 * 1024) {
        Utils.toast(`«${f.name}» supera ${MAX_ADJUNTO_MB} MB`, 'error');
        continue;
      }
      nuevos.push(f);
    }
    if (nuevos.length) setAdjuntos((a) => [...a, ...nuevos]);
  };

  const enviar = () => {
    const { html, texto } = leerEditor(editorRef.current);
    if (!texto) {
      Utils.toast('Escribe algo para publicar', 'warning');
      return;
    }
    // El aviso amable antes de mandarlo: el servidor valida contra la misma
    // lista y devolvería un 400 seco.
    if (Utils.findProfanity(texto)) {
      Utils.toast('Tu publicación contiene lenguaje no permitido. Por favor edítala.', 'error', 4500);
      return;
    }

    publicar.mutate(
      { contenido: html, tipo, imagen, adjuntos, comoArea, nombrePublico },
      {
        onSuccess: (r) => {
          if (r.imagenFallo) {
            Utils.toast('La publicación se creó, pero la imagen no se pudo subir', 'warning');
          }
          // Vaciar el compositor: el editor se limpia subiendo la versión.
          setVersion((v) => v + 1);
          setLargo(0);
          setTipo('general');
          setImagen(null);
          setAdjuntos([]);
          setComoArea(false);
          setNombrePublico('');
          if (entradaImagen.current) entradaImagen.current.value = '';
          if (entradaArchivos.current) entradaArchivos.current.value = '';

          if (r.fallidos.length) {
            // La publicación ya está en el muro y `POST /attachments` es
            // reintentable. Antes esto era un toast que desaparecía: la persona
            // se quedaba con la publicación arriba, sin su archivo y sin forma
            // de arreglarlo salvo borrarla y empezar de nuevo.
            setPostFallido(r.id);
            setFallidos(r.fallidos);
          } else {
            Utils.toast('Publicado exitosamente', 'success');
          }
          alPublicar();
        },
        onError: (e: unknown) =>
          Utils.toast((e as Error).message || 'No se pudo publicar', 'error'),
      },
    );
  };

  return (
    <section className="rounded-md border border-line bg-surface p-4" aria-label="Escribir una publicación">
      <div className="flex gap-3">
        <Avatar nombre={yo?.full_name} tamano={44} />
        <div className="min-w-0 flex-1" ref={editorRef}>
          <EditorRico
            placeholder="¿Qué quieres compartir con el equipo?"
            etiqueta="Contenido de la publicación"
            version={version}
            onCambio={(t) => setLargo(t.length)}
            // La lista de palabras vetadas se trae al ENFOCAR el editor, no al
            // montar la pantalla. `page-feed.js` la pedía al entrar, así que
            // todo el que abría el muro solo para leerlo se descargaba la lista
            // de moderación. Al enfocar quedan segundos de sobra antes de que
            // alguien pueda pulsar Publicar, y `loadProfanityList` es
            // idempotente: si ya llegó, no vuelve a pedirse.
            onFoco={() => void Utils.loadProfanityList()}
          />
        </div>
      </div>

      {/* ── Vista previa de la imagen ───────────────────────────────── */}
      {vistaPrevia && (
        <div className="relative mt-3 w-fit">
          <img
            src={vistaPrevia}
            alt="Vista previa de la imagen elegida"
            className="max-h-60 rounded-sm border border-line object-contain"
          />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className="absolute right-1 top-1 size-8 rounded-full bg-surface/90"
            aria-label="Quitar la imagen"
            onClick={() => {
              setImagen(null);
              if (entradaImagen.current) entradaImagen.current.value = '';
            }}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </Button>
        </div>
      )}

      {/* ── Archivos elegidos ───────────────────────────────────────── */}
      {adjuntos.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {adjuntos.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-xs bg-sunken px-2 py-1 text-2 text-ink-2"
            >
              <i className={cn('bi', iconoDeArchivo(f.type, f.name))} aria-hidden="true" />
              <span className="max-w-50 truncate">{f.name}</span>
              {/* El peso faltaba: el compositor rechazaba en silencio lo que
                  pasaba de 25 MB y no decía cuánto pesaba lo que sí aceptó. */}
              <span className="text-muted">{pesoLegible(f.size)}</span>
              <button
                type="button"
                aria-label={`Quitar ${f.name}`}
                onClick={() => setAdjuntos((a) => a.filter((_, k) => k !== i))}
                className="rounded-xs px-0.5 text-muted transition-colors hover:text-danger-ink
                           focus-visible:focus-anillo"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* ── Aviso de alcance ────────────────────────────────────────── */}
      {puedeComunicar && (
        <div
          className="mt-3 flex flex-wrap items-center gap-2 rounded-sm border border-line
                     bg-sunken px-3 py-2 text-2 text-ink-2"
        >
          <i className="bi bi-megaphone-fill text-brand-ink" aria-hidden="true" />
          <span className="min-w-45 flex-1">
            Lo que publiques aquí lo ve <strong>todo el portal</strong>.
            {(hayComunidades || hayComunicaciones) && ' Para dirigirlo a un grupo:'}
          </span>
          {hayComunidades && (
            <Button variant="ghost" size="sm" onClick={() => APP.navigate('comunidades')}>
              <i className="bi bi-people-fill" aria-hidden="true" /> Comunidades
            </Button>
          )}
          {hayComunicaciones && (
            /* `centro-notificaciones`, que es el alias del HASH_ALIAS.
               Hasta el 2026-09-15 esto decía `admin/comunicaciones`, y esa ruta
               NO existe: `navigate()` parte por la barra, resuelve `admin` y
               abre el **Panel de administración**. El botón llevaba año y
               medio de portal a la pantalla equivocada sin dar ningún error, y
               a quien no tuviera el módulo `administracion` lo echaba a Inicio
               con «No tienes acceso a esta sección». */
            <Button variant="ghost" size="sm" onClick={() => APP.navigate('centro-notificaciones')}>
              <i className="bi bi-send-fill" aria-hidden="true" /> Comunicaciones
            </Button>
          )}
        </div>
      )}

      {/* ── Firmar a nombre del área ────────────────────────────────── */}
      {puedeFirmarComoArea && (
        <div className="mt-3 rounded-sm border border-line bg-sunken px-3 py-2.5">
          <label className="flex cursor-pointer items-center gap-2 text-2 text-ink-2">
            <input
              type="checkbox"
              checked={comoArea}
              onChange={(e) => setComoArea(e.target.checked)}
              className="size-4 accent-[var(--brand)]"
            />
            <i className="bi bi-buildings-fill text-brand-ink" aria-hidden="true" />
            <span>
              Publicar a nombre del área <strong>(sale marcada como OFICIAL)</strong>
            </span>
          </label>
          {comoArea && (
            <div className="mt-2">
              <label className="block">
                <span className="sr-only">Nombre con el que firma</span>
                <input
                  type="text"
                  maxLength={120}
                  value={nombrePublico}
                  onChange={(e) => setNombrePublico(e.target.value)}
                  placeholder="Nombre con el que firma (opcional)"
                  className="w-full rounded-sm border border-field-border bg-surface px-3 py-2
                             text-2 text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
                />
              </label>
              <p className="mt-1 text-2 text-muted">
                Si lo dejas vacío se usa el nombre institucional configurado. Tu nombre queda
                registrado para la administración.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Barra inferior ──────────────────────────────────────────── */}
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Tipo de publicación">
          {TIPOS_QUE_SE_PUEDEN_ELEGIR.map((t) => (
            <button
              key={t.clave}
              type="button"
              aria-pressed={tipo === t.clave}
              onClick={() => setTipo(t.clave)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-2',
                'font-semibold transition-colors focus-visible:focus-anillo',
                'pointer-coarse:min-h-11',
                tipo === t.clave
                  ? 'border-brand-ink bg-brand text-on-brand'
                  : 'border-line text-ink-2 hover:bg-sunken',
              )}
            >
              <i className={cn('bi', t.icono)} aria-hidden="true" />
              {t.etiqueta}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <input
            ref={entradaImagen}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            hidden
            onChange={(e) => elegirImagen(e.target.files?.[0])}
          />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Adjuntar imagen"
            title="Adjuntar imagen"
            onClick={() => entradaImagen.current?.click()}
          >
            <i className="bi bi-image" aria-hidden="true" />
          </Button>

          <input
            ref={entradaArchivos}
            type="file"
            multiple
            hidden
            accept=".pdf,.xlsx,.xls,.csv,.txt,.doc,.docx,image/*,application/pdf"
            onChange={(e) => {
              elegirArchivos(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Adjuntar archivos"
            title="Adjuntar archivos (PDF, Excel, fotos, notas)"
            onClick={() => entradaArchivos.current?.click()}
          >
            <i className="bi bi-paperclip" aria-hidden="true" />
          </Button>

          {largo >= MAX_CARACTERES * AVISO_DESDE && (
            <span
              className={cn(
                'text-1 tabular-nums',
                excede ? 'font-bold text-danger-ink' : 'text-warn-ink',
              )}
              role={excede ? 'alert' : undefined}
            >
              {largo.toLocaleString('es-CO')} / {MAX_CARACTERES.toLocaleString('es-CO')}
            </span>
          )}

          <Button
            loading={publicar.isPending}
            disabled={excede || vacio}
            razonDeshabilitado={
              excede
                ? `Te pasaste del límite de ${MAX_CARACTERES.toLocaleString('es-CO')} caracteres`
                : 'Escribe algo para poder publicarlo'
            }
            onClick={enviar}
          >
            <i className="bi bi-send-fill" aria-hidden="true" /> Publicar
          </Button>
        </div>
      </div>

      {fallidos && postFallido != null && (
        <DialogoDeReintento
          postId={postFallido}
          archivos={fallidos}
          onCerrar={() => {
            setFallidos(null);
            setPostFallido(null);
          }}
        />
      )}
    </section>
  );
}

/* ── Reintento de adjuntos ─────────────────────────────────────────────── */

/**
 * Los archivos que no subieron, con un botón para reintentarlos.
 *
 * La publicación ya está en el muro y el endpoint de adjuntos es reintentable.
 * Sin esto, la única salida era borrar la publicación y escribirla otra vez.
 */
function DialogoDeReintento({
  postId, archivos, onCerrar,
}: {
  postId: number;
  archivos: File[];
  onCerrar: () => void;
}) {
  const [reintentando, setReintentando] = useState(false);

  const reintentar = async () => {
    setReintentando(true);
    const siguen = await subirAdjuntos(postId, archivos);
    setReintentando(false);
    if (siguen.length) {
      Utils.toast(
        `Siguen sin subir ${siguen.length} archivo(s). Puedes adjuntarlos luego desde la publicación.`,
        'error',
        6000,
      );
    } else {
      Utils.toast('Archivos adjuntados', 'success');
    }
    onCerrar();
  };

  return (
    <Dialog
      abierto
      onCambio={onCerrar}
      titulo="Faltaron archivos por adjuntar"
      descripcion={`Tu publicación ya está en el muro, pero ${
        archivos.length === 1 ? 'este archivo no subió' : 'estos archivos no subieron'
      }. Puedes reintentar sin volver a escribirla.`}
      ancho="max-w-md"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>
            Dejarlo así
          </Button>
          <Button loading={reintentando} onClick={() => void reintentar()} autoFocus>
            <i className="bi bi-arrow-clockwise" aria-hidden="true" /> Reintentar
          </Button>
        </>
      }
    >
      <ul className="flex flex-col gap-1">
        {archivos.map((f, i) => (
          <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-3 text-ink-2">
            <i className={cn('bi', iconoDeArchivo(f.type, f.name))} aria-hidden="true" />
            <span className="min-w-0 truncate">{f.name}</span>
            <span className="ml-auto shrink-0 text-2 text-muted">{pesoLegible(f.size)}</span>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
